/**
 * Rooms: shapes, attach beyond a door (F9), split (F11), naming, rotate 90,
 * mirror, duplicate, delete, rename, ceiling.
 */
import { POLICY } from './policy';
import { remapWalls, setWallIn, stampNewRoom, wallFtOf, wallIn } from './thickness';
import { missingFields } from './missingKinds';
import { firstRoomDoor } from './place';
import { shiftCeilingTo } from './ceiling';
import {
  EPS, axisOf, clone, dist, edgeOf, findRoom, labelPt, outN, round6, snapIn, tidy, unit, RECT_CORNERS,
} from './geom';
import { roomId, uid } from './plan';
import { adjacent, syncLinked } from './shared';
import { releaseMissingWalls } from './wallRelease';
import { JAMB_MESSAGE, splitMissingAtJunctions } from './wallMeetsOpening';
import type { Axis, Corner, Fail, Opening, Plan, Pt, Room, ShapeName } from './types';

/** Shape outline at the origin, from the policy sizes (F2). */
export function shapePts(shape: ShapeName): [number, number][] {
  if (shape === 'L') {
    const s = POLICY.shapes.L, W = s.width_ft, D = s.depth_ft;
    const nw = s.notch_width_ft ?? 0, nd = s.notch_depth_ft ?? 0;
    return [[0, 0], [W, 0], [W, D - nd], [W - nw, D - nd], [W - nw, D], [0, D]];
  }
  const s = POLICY.shapes.rectangle;
  return [[0, 0], [s.width_ft, 0], [s.width_ft, s.depth_ft], [0, s.depth_ft]];
}
export const shapeWidth = (shape: ShapeName): number =>
  shape === 'L' ? POLICY.shapes.L.width_ft : POLICY.shapes.rectangle.width_ft;

export function makeRoom(plan: Plan, name: string, pts: [number, number][], opts: { raw?: boolean; id?: string } = {}): Room {
  const f = opts.raw ? round6 : snapIn;
  const r: Room = {
    id: roomId(plan, opts.id), name, level: POLICY.level_default, ceilingFt: plan.defaults.ceilingFt,
    corners: pts.map((p) => ({ id: uid(plan), x: f(p[0]), y: f(p[1]) })),
    measurements: [], openings: [],
  };
  stampNewRoom(plan, r);
  return r;
}

/**
 * A room built from `pts` (x along wall `vid` of `src` from `startFt`, y outward),
 * one stored wall thickness out. Its first wall faces `vid` and takes that thickness,
 * so the two are one shared wall (FIELD-2 E6), and is its `attachedEdge` (FIELD-3 J18).
 */
export function roomOffWall(
  plan: Plan, src: Room, vid: number, startFt: number, pts: [number, number][], name: string, id?: string,
): Room {
  const E = edgeOf(src, vid)!;
  const t = unit(E.a, E.b), n = outN(src, E.a, E.b), T = wallFtOf(src, vid);
  const o = { x: E.a.x + t.x * startFt + n.x * T, y: E.a.y + t.y * startFt + n.y * T };
  const r = makeRoom(plan, name, pts.map(([u, w]) => [o.x + t.x * u + n.x * w, o.y + t.y * u + n.y * w]), { raw: true, id });
  setWallIn(r, r.corners[0].id, wallIn(src, vid));
  r.attachedEdge = r.corners[0].id; // FIELD-3 J18: measuring it moves the opposite wall
  return r;
}

/** A detached room centred on a world point. */
export function freeRoom(plan: Plan, shape: ShapeName, center: Pt, name: string, id?: string): Room {
  const pts = shapePts(shape);
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  const o = { x: snapIn(center.x - Math.max(...xs) / 2), y: snapIn(center.y - Math.max(...ys) / 2) };
  return makeRoom(plan, name, pts.map(([u, w]) => [o.x + u, o.y + w]), { id });
}

/**
 * Where along the wall a new room of width W starts (F9, PRP case 14). It lines
 * up with the wall corner nearest the door (or tap point) when the room fits
 * on the wall there and still covers the door; otherwise it centres on the door.
 * (The prototype clamped the centred position to the wall instead; the PRP's
 * wording wins, D9.)
 */
export function attachStart(L: number, W: number, center: number, doorW = 0): number {
  if (W <= L) {
    const st = center <= L / 2 ? 0 : L - W;
    if (center - doorW / 2 >= st - 1e-9 && center + doorW / 2 <= st + W + 1e-9) return st;
  }
  return center - W / 2;
}

/**
 * A room off wall `vid` of `src`, one wall thickness out, placed per
 * `attachStart`. With a door, the new room gets the linked copy.
 */
export function attachRoom(
  plan: Plan, src: Room, vid: number, center: number, shape: ShapeName, name: string,
  door?: Opening | null, id?: string,
): Room {
  const E = edgeOf(src, vid)!;
  const W = shapeWidth(shape);
  const L = dist(E.a, E.b);
  const st = snapIn(attachStart(L, W, center, door ? door.widthFt : 0));
  const r = roomOffWall(plan, src, vid, st, shapePts(shape), name, id);
  linkDoorCopy(plan, r, W, door);
  return r;
}

/** The new room's linked copy of the door it was added through (placed by `syncLinked`). */
export function linkDoorCopy(plan: Plan, r: Room, W: number, door?: Opening | null): void {
  if (door) {
    if (door.link == null) door.link = uid(plan);
    r.openings.push({ ...clone(door), id: uid(plan), edgeStart: r.corners[0].id, offFt: W / 2 - door.widthFt / 2 });
  }
}

/** F9 naming: Bedroom 1, Bedroom 2...; others get a number from the second on. */
export function nextName(plan: Plan, base: string): string {
  if (!POLICY.numbered_room_names.includes(base)) {
    const same = plan.rooms.filter((r) => r.name === base || r.name.startsWith(`${base} `)).length;
    return same ? `${base} ${same + 1}` : base;
  }
  const n = plan.rooms.filter((r) => r.name.startsWith(`${base} `)).length;
  return `${base} ${n + 1}`;
}

type Added = { ok: true; rid: string };

/**
 * A detached room. The plan's first room gets an exterior door centred on its
 * bottom wall when the policy says so (G6); `noDoor` skips it.
 */
export function addFreeRoom(
  plan: Plan, shape: ShapeName, center: Pt, opts: { id?: string; name?: string; noDoor?: boolean } = {},
): Added & { doorId?: number } {
  const first = plan.rooms.length === 0;
  const r = freeRoom(plan, shape, center, opts.name ?? POLICY.new_room_name, opts.id);
  plan.rooms.push(r);
  tidy(plan);
  if (first && POLICY.first_room_exterior_door && !opts.noDoor) {
    const d = firstRoomDoor(plan, r.id);
    if (d != null) return { ok: true, rid: r.id, doorId: d };
  }
  return { ok: true, rid: r.id };
}

/** "Add room here" on a wall, centred at offset `center` along it. */
export function addRoomOffWall(
  plan: Plan, rid: string, vid: number, center: number, shape: ShapeName, opts: { id?: string; name?: string } = {},
): Added | Fail<'missing'> {
  const src = findRoom(plan, rid);
  if (!src || !edgeOf(src, vid)) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const r = attachRoom(plan, src, vid, center, shape, opts.name ?? POLICY.new_room_name, null, opts.id);
  plan.rooms.push(r);
  tidy(plan);
  return { ok: true, rid: r.id };
}

/**
 * The room beyond a door (FIELD-2 E11). A rectangle's attached wall is the door's
 * whole wall, `beyond_depth_ft` deep; on a wall longer than `beyond_match_max_ft`
 * it is the FIELD-1 rectangle centred on the door. An L keeps FIELD-1's placement.
 */
export function attachBeyond(plan: Plan, src: Room, door: Opening, shape: ShapeName, name: string, id?: string): Room {
  const E = edgeOf(src, door.edgeStart)!;
  const L = dist(E.a, E.b), center = door.offFt + door.widthFt / 2;
  if (shape !== 'rect') return attachRoom(plan, src, door.edgeStart, center, shape, name, door, id);
  const D = POLICY.beyond_depth_ft;
  const match = L <= POLICY.beyond_match_max_ft + EPS;
  const W = match ? L : shapeWidth(shape);
  const pts: [number, number][] = match ? [[0, 0], [L, 0], [L, D], [0, D]] : shapePts(shape);
  const r = roomOffWall(plan, src, door.edgeStart, match ? 0 : snapIn(center - W / 2), pts, name, id);
  linkDoorCopy(plan, r, W, door);
  return r;
}

/** F9: tap a door (or missing wall), Add room beyond (sized per E11). */
export function addRoomBeyond(
  plan: Plan, rid: string, oid: number, shape: ShapeName, opts: { id?: string; name?: string } = {},
): Added | Fail<'missing' | 'occupied'> {
  const src = findRoom(plan, rid);
  const o = src?.openings.find((q) => q.id === oid);
  if (!src || !o) return { ok: false, err: 'missing', message: 'That opening is gone' };
  if (adjacent(plan, src, o)) return { ok: false, err: 'occupied', message: 'There is already a room on the other side' };
  const r = attachBeyond(plan, src, o, shape, opts.name ?? POLICY.new_room_name, opts.id);
  plan.rooms.push(r);
  syncLinked(plan, src, o);
  tidy(plan);
  return { ok: true, rid: r.id };
}

/**
 * F11: split a room along the line through the point at `off` on wall `vid`,
 * perpendicular to that wall. FIELD-3 J16: the halves are two ordinary rooms. The
 * half holding the original room's label point keeps its id (photos, dictations
 * and checklist stay with it); the other is new (`id`). Nothing links them but the
 * split line: a 0 in shared wall missing end to end on both sides (E12, E13), linked,
 * recording that each room absorbed 0 in (J1) so Restore wall (J4) works on it.
 * Measurements whose two corners land in one half stay with it; one across the line
 * is dropped (FIELD-2 kept it as a `spans` record tying the halves; J16 removes it).
 * An opening on a wall the line cuts goes to the half its middle is in.
 */
export function doSplit(
  plan: Plan, rid: string, vid: number, off: number, opts: { id?: string; name?: string } = {},
): { ok: true; rid: string; newRid: string; jamb?: string; splitMissing?: number } | Fail<'missing' | 'not-straight' | 'too-close' | 'no-split' | 'through-opening'> {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  if (!r || !E) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const t = axisOf(E.a, E.b);
  if (t !== 'h' && t !== 'v') return { ok: false, err: 'not-straight', message: 'Pick a straight wall' };
  const u = unit(E.a, E.b);
  const p = { x: E.a.x + u.x * off, y: E.a.y + u.y * off };
  const ax: Axis = t === 'h' ? 'x' : 'y';
  let c = snapIn(p[ax]);
  const vals = r.corners.map((v) => v[ax]);
  const clr = POLICY.split_clearance_ft;
  if (c <= Math.min(...vals) + clr || c >= Math.max(...vals) - clr) {
    return { ok: false, err: 'too-close', message: 'Too close to the end of the room. Tap farther in.' };
  }
  // W3 (DD-SF3.14): never through a door or window; touching one end to end is fine.
  // FIELD-4 K7: the line stops at the nearer jamb instead (with the message), and a
  // missing wall it crosses splits into two, one per room (`splitMissingAtJunctions`).
  let jamb: string | undefined;
  const door = r.openings.find((o) => o.kind !== 'missing' && crossesLine(r, o, ax, c));
  if (door) {
    const ends = openingEnds(r, door, ax);
    const to = Math.abs(ends[0] - c) <= Math.abs(ends[1] - c) ? ends[0] : ends[1];
    if (to > Math.min(...vals) + clr && to < Math.max(...vals) - clr) { c = round6(to); jamb = JAMB_MESSAGE; }
  }
  const cut = r.openings.find((o) => o.kind !== 'missing' && crossesLine(r, o, ax, c));
  if (cut) {
    const what = SPLIT_KIND_WORD[cut.kind];
    return { ok: false, err: 'through-opening', message: `The split line runs through ${what}. Move the ${what.split(' ')[1]} or split somewhere else.` };
  }
  let next = plan.next;
  const xFrom = new Map<number, number>(); // a new corner -> the wall it was cut from
  const halves = [true, false].map((keepLess) => {
    const ins = (q: Pt) => (keepLess ? q[ax] <= c + 1e-9 : q[ax] >= c - 1e-9);
    const out: Corner[] = [];
    const n = r.corners.length;
    for (let i = 0; i < n; i++) {
      const a = r.corners[i], b = r.corners[(i + 1) % n], ai = ins(a), bi = ins(b);
      const X = (): Corner => {
        const k = (c - a[ax]) / (b[ax] - a[ax]);
        const q = { id: next++, x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k };
        q[ax] = c;
        xFrom.set(q.id, a.id);
        return q;
      };
      if (ai && bi) out.push({ ...b });
      else if (ai && !bi) out.push(X());
      else if (!ai && bi) { out.push(X()); out.push({ ...b }); }
    }
    const cl: Corner[] = [];
    out.forEach((q) => { const l = cl[cl.length - 1]; if (!l || dist(l, q) > 1e-6) cl.push(q); });
    if (cl.length > 1 && dist(cl[0], cl[cl.length - 1]) < 1e-6) cl.pop();
    return cl;
  });
  if (halves.some((h) => h.length < RECT_CORNERS)) return { ok: false, err: 'no-split', message: 'That split did not make two rooms' };
  plan.next = next;
  // J16: the half with the room's label point keeps the id.
  // (A label point on the line itself stays with the low side, as before.)
  const baseIdx = labelPt(r)[ax] > c + EPS ? 1 : 0;
  const mk = (pts: Corner[], base: boolean): Room => {
    const nr: Room = {
      ...clone(r), id: base ? r.id : roomId(plan, opts.id), name: base ? r.name : (opts.name ?? POLICY.new_room_name),
      corners: pts, measurements: [], openings: [],
    };
    if (!base) {
      delete nr.banner; delete nr.unplaced; delete nr.subrooms; delete nr.primary; delete nr.second; delete nr.checkDims;
    }
    delete nr.spans;
    const ids = new Set(pts.map((q) => q.id));
    if (nr.attachedEdge != null && !ids.has(nr.attachedEdge)) delete nr.attachedEdge;
    ownOrientation(nr, r, xFrom);
    nr.measurements = r.measurements.filter((m) => ids.has(m.a) && ids.has(m.b)).map((m) => ({ ...m }));
    r.openings.forEach((o) => {
      // The wall's piece in this half: the wall itself, or the piece cut from it.
      const own = ids.has(o.edgeStart);
      const sid = own ? o.edgeStart : pts.find((q) => xFrom.get(q.id) === o.edgeStart)?.id;
      if (sid == null) return;
      const oe = edgeOf(r, o.edgeStart)!, ne = edgeOf(nr, sid);
      if (!ne) return;
      const ou = unit(oe.a, oe.b), nu = unit(ne.a, ne.b);
      if (ou.x * nu.x + ou.y * nu.y < POLICY.parallel_dot_min) return;
      const at = o.offFt - (own ? 0 : dist(oe.a, ne.a)), L = dist(ne.a, ne.b);
      const mid = at + o.widthFt / 2;
      // Its middle is in the other half; a missing wall the line crosses goes to both (K7).
      if ((mid < 0 || mid > L) && o.kind !== 'missing') return;
      const s0 = Math.max(0, at), s1 = Math.min(L, at + o.widthFt);
      if (s1 - s0 < POLICY.opening_clearance_ft) return;
      nr.openings.push({ ...clone(o), edgeStart: sid, offFt: s0, widthFt: s1 - s0 });
    });
    // FIELD-2: every piece keeps its wall's thickness; the split line is a 0 in
    // shared wall missing end to end (E12, E13), so the halves stay one shared wall.
    nr.walls = {};
    const n = pts.length;
    for (let i = 0; i < n; i++) {
      const a = pts[i], b = pts[(i + 1) % n];
      if (Math.abs(a[ax] - c) < EPS && Math.abs(b[ax] - c) < EPS && dist(a, b) > POLICY.min_segment_ft) {
        setWallIn(nr, a.id, 0);
        nr.openings.push({
          id: uid(plan), kind: 'missing', edgeStart: a.id, offFt: 0, widthFt: dist(a, b),
          missing: missingFields(POLICY.open_to_kind, nr.ceilingFt),
        });
      } else {
        const from = xFrom.get(a.id) ?? a.id;
        const w = r.walls?.[from];
        if (w) nr.walls[a.id] = { ...w };
      }
    }
    return nr;
  };
  const A = mk(halves[baseIdx], true), B = mk(halves[1 - baseIdx], false);
  linkSplitLine(plan, A, B, ax, c);
  plan.rooms.splice(plan.rooms.indexOf(r), 1, A, B);
  tidy(plan);
  const split = splitMissingAtJunctions(plan);
  return { ok: true, rid: A.id, newRid: B.id, ...(jamb ? { jamb } : {}), ...(split.length ? { splitMissing: split.length } : {}) };
}

/** The refusal's words per opening kind (W3). */
const SPLIT_KIND_WORD: Record<Opening['kind'], string> = { door: 'a door', window: 'a window', missing: 'an opening' };

/** Opening `o`'s two ends on axis `ax`, low then high. */
function openingEnds(r: Room, o: Opening, ax: Axis): [number, number] {
  const E = edgeOf(r, o.edgeStart)!;
  const u = unit(E.a, E.b);
  const p0 = E.a[ax] + u[ax] * o.offFt, p1 = E.a[ax] + u[ax] * (o.offFt + o.widthFt);
  return [Math.min(p0, p1), Math.max(p0, p1)];
}

/** Opening `o` runs across the line `ax = c` (strictly: an end on the line only touches it). */
function crossesLine(r: Room, o: Opening, ax: Axis, c: number): boolean {
  const E = edgeOf(r, o.edgeStart);
  if (!E) return false;
  const u = unit(E.a, E.b);
  const p0 = E.a[ax] + u[ax] * o.offFt, p1 = E.a[ax] + u[ax] * (o.offFt + o.widthFt);
  return Math.min(p0, p1) < c - EPS && Math.max(p0, p1) > c + EPS;
}

/**
 * A non-flat ceiling's orientation wall in a half (J16: never a corner of the
 * other half): the piece of the original wall in this half, else this half's wall
 * parallel to it nearest its line, else the ceiling is dropped to flat.
 */
function ownOrientation(nr: Room, r: Room, xFrom: Map<number, number>): void {
  const o = (nr.ceiling as { orientation?: { edgeStart: number } } | undefined)?.orientation;
  if (!o || nr.corners.some((q) => q.id === o.edgeStart)) return;
  const E = edgeOf(r, o.edgeStart);
  let to = nr.corners.find((q) => xFrom.get(q.id) === o.edgeStart)?.id;
  if (to == null && E) {
    const t = axisOf(E.a, E.b), lx: Axis = t === 'h' ? 'y' : 'x';
    let best = Infinity;
    nr.corners.forEach((a, i) => {
      const b = nr.corners[(i + 1) % nr.corners.length];
      if (axisOf(a, b) !== t || Math.abs(a[lx] - E.a[lx]) >= best) return;
      best = Math.abs(a[lx] - E.a[lx]);
      to = a.id;
    });
  }
  if (to != null) o.edgeStart = to; else delete nr.ceiling;
}

/** Link the two halves' missing walls on the split line (one pair per piece of line). */
function linkSplitLine(plan: Plan, A: Room, B: Room, ax: Axis, c: number): void {
  const ox: Axis = ax === 'x' ? 'y' : 'x';
  const span = (r: Room, o: Opening): [number, number] | null => {
    const E = edgeOf(r, o.edgeStart);
    if (!E || Math.abs(E.a[ax] - c) > EPS || Math.abs(E.b[ax] - c) > EPS) return null;
    return [Math.min(E.a[ox], E.b[ox]), Math.max(E.a[ox], E.b[ox])];
  };
  for (const oa of A.openings) {
    const sa = oa.kind === 'missing' && oa.link == null ? span(A, oa) : null;
    if (!sa) continue;
    const ob = B.openings.find((o) => {
      const sb = o.kind === 'missing' && o.link == null ? span(B, o) : null;
      return !!sb && Math.abs(sb[0] - sa[0]) < EPS && Math.abs(sb[1] - sa[1]) < EPS;
    });
    if (!ob) continue;
    oa.link = uid(plan);
    ob.link = oa.link;
    // J1 with a 0 in wall: both rooms absorbed 0 in, so Restore wall (J4) splits the new wall evenly.
    const absorbed = [{ roomId: A.id, distanceIn: 0 }, { roomId: B.id, distanceIn: 0 }];
    oa.missing = { ...oa.missing!, absorbed };
    ob.missing = { ...ob.missing!, absorbed: clone(absorbed) };
  }
}

const missingRoom = (): Fail<'missing'> => ({ ok: false, err: 'missing', message: 'That room is gone' });

/** Rotate 90 degrees about the room's (snapped) bounding-box centre. */
export function rotateRoom90(plan: Plan, rid: string): { ok: true } | Fail<'missing'> {
  const r = findRoom(plan, rid);
  if (!r) return missingRoom();
  const xs = r.corners.map((p) => p.x), ys = r.corners.map((p) => p.y);
  const cx = snapIn((Math.min(...xs) + Math.max(...xs)) / 2), cy = snapIn((Math.min(...ys) + Math.max(...ys)) / 2);
  r.corners.forEach((p) => { const x = p.x - cx, y = p.y - cy; p.x = cx - y; p.y = cy + x; });
  r.measurements.forEach((m) => { m.axis = m.axis === 'x' ? 'y' : 'x'; });
  tidy(plan);
  return { ok: true };
}

/** Mirror left-right about the bounding-box centre. */
export function mirrorRoom(plan: Plan, rid: string): { ok: true } | Fail<'missing'> {
  const r = findRoom(plan, rid);
  if (!r) return missingRoom();
  const xs = r.corners.map((p) => p.x);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  r.corners.forEach((p) => { p.x = snapIn(2 * cx - p.x); });
  tidy(plan);
  return { ok: true };
}

/** Copy offset by the policy distance, renamed with the next number. Links are dropped. */
export function duplicateRoom(plan: Plan, rid: string, opts: { id?: string } = {}): { ok: true; rid: string } | Fail<'missing'> {
  const r = findRoom(plan, rid);
  if (!r) return missingRoom();
  const c = clone(r);
  c.id = roomId(plan, opts.id);
  const map = new Map<number, number>();
  const dd = POLICY.duplicate_offset_ft;
  c.corners.forEach((p) => { const n = uid(plan); map.set(p.id, n); p.id = n; p.x += dd; p.y += dd; });
  remapWalls(c, map);
  delete c.checkDims;
  delete c.spans; // they tie the original to its split partner, not the copy
  delete c.attachedEdge; // a copy is not attached (FIELD-3 J18)
  delete c.damage; // its corners are new ids: a copy is not damaged (FIELD-5)
  c.measurements.forEach((m) => { m.a = map.get(m.a)!; m.b = map.get(m.b)!; });
  c.openings.forEach((o) => { o.id = uid(plan); o.edgeStart = map.get(o.edgeStart)!; delete o.link; });
  c.name = nextName(plan, r.name.replace(/\s\d+$/, ''));
  plan.rooms.push(c);
  tidy(plan);
  return { ok: true, rid: c.id };
}

/**
 * Delete a room. Linked doors and windows in other rooms stay (each keeps its copy,
 * F7); a missing wall to it is undone from the other side first (W5,
 * `releaseMissingWalls`): `released` names the rooms that got a wall back.
 */
export function deleteRoom(plan: Plan, rid: string, opts: { now?: string } = {}): { ok: true; released: string[] } | Fail<'missing'> {
  const r = findRoom(plan, rid);
  if (!r) return missingRoom();
  const released = releaseMissingWalls(plan, r, opts.now);
  plan.rooms = plan.rooms.filter((q) => q !== r);
  return { ok: true, released };
}

/** Rename. A name from the policy's numbered list is numbered automatically. */
export function renameRoom(plan: Plan, rid: string, name: string, opts: { fromChip?: boolean } = {}): { ok: true; name: string } | Fail<'missing' | 'empty'> {
  const r = findRoom(plan, rid);
  if (!r) return missingRoom();
  const v = name.trim();
  if (!v) return { ok: false, err: 'empty', message: 'Type a name or pick one above' };
  r.name = opts.fromChip ? nextName(plan, v) : v;
  return { ok: true, name: r.name };
}

/** Ceiling height; also becomes the default for new rooms (prototype). */
export function setCeiling(plan: Plan, rid: string, ft: number): { ok: true } | Fail<'missing' | 'too-low'> {
  const r = findRoom(plan, rid);
  if (!r) return missingRoom();
  if (ft < POLICY.ceiling_min_ft) return { ok: false, err: 'too-low', message: `Ceiling must be at least ${POLICY.ceiling_min_ft} feet` };
  shiftCeilingTo(r, ft); // a non-flat ceiling keeps its shape (FIELD-2 E18)
  r.ceilingFt = ft;
  delete r.ceilingFromDefault;
  plan.defaults.ceilingFt = ft;
  return { ok: true };
}
